import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

export default function RegisterPage({ isPartner = false }) {
  const { register, registerPartner } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', company_email: '', company: '', password: '', partner_type: '' });
  const [loading, setLoading] = useState(false);

  const PARTNER_TYPES = [
    { value: 'distributor', label: 'Distributor' },
    { value: 'integrator', label: 'System Integrator' },
    { value: 'vad', label: 'VAD' },
    { value: 'isv', label: 'ISV' },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company_email) {
      toast({ title: 'Missing fields', description: 'Name and company email are required.' });
      return;
    }
    if (isPartner && !form.partner_type) {
      toast({ title: 'Missing fields', description: 'Please select a partner type.' });
      return;
    }
    setLoading(true);
    try {
      if (isPartner) {
        const res = await registerPartner(form);
        toast({ title: 'Partner account created', description: res.message || 'Approval pending.' });
        navigate('/partner/dashboard');
      } else {
        const u = await register(form);
        toast({ title: 'Account created', description: `Welcome, ${u.name}` });
        navigate('/dashboard');
      }
    } catch (err) {
      toast({ title: 'Registration failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[80vh] flex items-center py-16">
      <div className="absolute inset-0 viso-grid opacity-30" />
      <div className="relative w-full max-w-md mx-auto px-6">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8">
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-3">{isPartner ? 'Partner Registration' : 'Create Account'}</div>
          <h1 className="font-display text-3xl font-bold">{isPartner ? 'Join the Visotonics partner program' : 'Get started with Visotonics'}</h1>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-slate-300">Full name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Company email *</Label>
              <Input type="email" required value={form.company_email} onChange={(e) => setForm({ ...form, company_email: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
            </div>
            {isPartner && (
              <div>
                <Label className="text-slate-300">Partner type *</Label>
                <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
                  <SelectTrigger className="bg-black/30 border-white/10 mt-1"><SelectValue placeholder="Select partner type" /></SelectTrigger>
                  <SelectContent>
                    {PARTNER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
            </div>
            <Button disabled={loading} className="w-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {isPartner ? 'Register as partner' : 'Create account'}
            </Button>
          </form>
          <div className="mt-6 text-sm text-slate-400 flex justify-between">
            <Link to="/login" className="hover:text-cyan-300">Have an account? Log in</Link>
            {!isPartner && <Link to="/partner/register" className="hover:text-cyan-300">Partner instead</Link>}
          </div>
        </div>
      </div>
    </div>
  );
}
